/**
 * Pattern Signals - System learns from community without surveillance
 *
 * Tracks aggregate patterns only:
 * - Which pillars generate most community examples
 * - Which levels generate longest pauses
 * - Which prompts are skipped vs engaged
 *
 * Constraints:
 * - No user-level tracking
 * - No identity linkage
 * - No behavioral scoring
 * - Aggregate only
 * - Used for curriculum tuning and gentle reflection
 *
 * This is listening, not analytics.
 */

const STORAGE_KEY = 'sifiso-pattern-signals';

/**
 * Get all pattern signals
 */
export function getPatternSignals() {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    return JSON.parse(stored);
  }

  // Initialize pattern structure
  return {
    examplesByPillar: {
      'digital': 0,
      'problem-solving': 0,
      'economic': 0,
      'ai': 0
    },
    examplesByLevel: {}, // levelId: count
    pausesByLevel: {}, // levelId: { total: ms, count: number }
    engagement: {
      teachBackCompleted: 0,
      teachBackSkipped: 0,
      contributionCompleted: 0,
      contributionSkipped: 0,
      applicationNoticed: 0 // When someone engages with "where might you use this"
    },
    totalLearners: 0, // Approximate unique learners (increment cautiously)
    lastUpdated: new Date().toISOString()
  };
}

/**
 * Save pattern signals
 */
function savePatternSignals(signals) {
  signals.lastUpdated = new Date().toISOString();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(signals));
}

/**
 * Track community example contribution
 */
export function trackExampleContribution(levelId, pillar) {
  const signals = getPatternSignals();

  if (pillar && signals.examplesByPillar[pillar] !== undefined) {
    signals.examplesByPillar[pillar]++;
  }

  if (!signals.examplesByLevel[levelId]) {
    signals.examplesByLevel[levelId] = 0;
  }
  signals.examplesByLevel[levelId]++;

  savePatternSignals(signals);
}

/**
 * Track pause duration in completion ritual
 */
export function trackPauseDuration(levelId, durationMs) {
  const signals = getPatternSignals();

  if (!signals.pausesByLevel[levelId]) {
    signals.pausesByLevel[levelId] = { total: 0, count: 0 };
  }

  signals.pausesByLevel[levelId].total += durationMs;
  signals.pausesByLevel[levelId].count++;

  savePatternSignals(signals);
}

/**
 * Track teach-back engagement
 */
export function trackTeachBack(completed) {
  const signals = getPatternSignals();

  if (completed) {
    signals.engagement.teachBackCompleted++;
  } else {
    signals.engagement.teachBackSkipped++;
  }

  savePatternSignals(signals);
}

/**
 * Track contribution engagement
 */
export function trackContribution(completed) {
  const signals = getPatternSignals();

  if (completed) {
    signals.engagement.contributionCompleted++;
  } else {
    signals.engagement.contributionSkipped++;
  }

  savePatternSignals(signals);
}

/**
 * Get gentle reflection for a level/pillar
 * Returns calm statement, never numbers
 */
export function getGentleReflection(levelId, pillar) {
  const signals = getPatternSignals();

  // Threshold for "many people" - only show if truly meaningful
  const MEANINGFUL_THRESHOLD = 3;

  const reflections = [];

  // Check if this pillar has meaningful community examples
  if (pillar && signals.examplesByPillar[pillar] >= MEANINGFUL_THRESHOLD) {
    const pillarNames = {
      'digital': 'Digital',
      'problem-solving': 'Problem-Solving',
      'economic': 'Economic',
      'ai': 'AI'
    };

    reflections.push({
      type: 'pillar-examples',
      text: `Many people noticed ${pillarNames[pillar]} thinking outside the app`
    });
  }

  // Check if this specific level has community examples
  if (signals.examplesByLevel[levelId] >= MEANINGFUL_THRESHOLD) {
    reflections.push({
      type: 'level-examples',
      text: 'This capacity has shown up in daily life for other learners'
    });
  }

  // Check if people pause at this level (longer than average)
  if (signals.pausesByLevel[levelId]) {
    const avgPause = signals.pausesByLevel[levelId].total / signals.pausesByLevel[levelId].count;
    // If average pause > 2 minutes, it's meaningful
    if (avgPause > 120000 && signals.pausesByLevel[levelId].count >= MEANINGFUL_THRESHOLD) {
      reflections.push({
        type: 'pause',
        text: 'Others have paused here too, reflecting on this moment'
      });
    }
  }

  // Return one reflection at random if any exist (variety, not all at once)
  if (reflections.length > 0) {
    const randomIndex = Math.floor(Math.random() * reflections.length);
    return reflections[randomIndex];
  }

  return null;
}

/**
 * Get system-wide insights (for curriculum tuning, not user display)
 * Developer-only, not shown to learners
 */
export function getSystemInsights() {
  const signals = getPatternSignals();

  // Calculate which pillar is most represented in community examples
  const topPillar = Object.entries(signals.examplesByPillar)
    .sort((a, b) => b[1] - a[1])[0];

  // Calculate teach-back completion rate
  const totalTeachBack = signals.engagement.teachBackCompleted + signals.engagement.teachBackSkipped;
  const teachBackRate = totalTeachBack > 0
    ? (signals.engagement.teachBackCompleted / totalTeachBack) * 100
    : 0;

  // Calculate contribution completion rate
  const totalContribution = signals.engagement.contributionCompleted + signals.engagement.contributionSkipped;
  const contributionRate = totalContribution > 0
    ? (signals.engagement.contributionCompleted / totalContribution) * 100
    : 0;

  return {
    topPillar: topPillar ? topPillar[0] : null,
    topPillarCount: topPillar ? topPillar[1] : 0,
    teachBackCompletionRate: teachBackRate,
    contributionCompletionRate: contributionRate,
    totalExamples: Object.values(signals.examplesByLevel).reduce((a, b) => a + b, 0),
    signals // Raw data for developer inspection
  };
}
